import PropTypes from 'prop-types';
import React, { useState } from 'react';

import AddOnRow from "./AddOnRow";

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Modal, Tooltip } from '@opendate-app/setlist';

const addOnIsSeated = (addOn) => {
  return (
    addOn
      && addOn.seating_chart_category_key
      && addOn.seating_chart_category_key.length > 0
  )
}

const totalNumberSold = (addOns) => {
  return addOns.reduce((sum, addOn) => {
    return sum + addOn.number_of_tickets_sold;
  }, 0);
}

const totalNumberAvailable = (addOns) => {
  return addOns.reduce((sum, addOn) => {
    return sum + addOn.available;
  }, 0);
}

const sortByPositionAndName = (addOns) => {
  return addOns.sort((a, b) =>{
    if (a.position !== b.position) {
      return a.position - b.position;
    } else {
      return a.name.localeCompare(b.name);
    }
  });
}

// The confirm's add-ons table, minus the rows: the series wizard renders its
// own rows inside the same frame so both lists look and reorder alike.
export const AddOnsTableFrame = ({ items, getId, onMove, renderRow, footer, isDragDisabled }) => (
  <div className="row">
    <div
      className="w-100 mt-3 mx-3 responsive-card-table-wrapper"
      style={{
        border: "1px solid var(--sl-border-secondary)",
        borderRadius: "12px",
        overflow: "hidden",
        overflowX: "auto",
      }}
    >
      <div className="table-responsive">
        <table
          className="table my-0 responsive-card-table addons-table"
          style={{
            color: "var(--sl-text-tertiary)",
            border: "none",
            fontSize: "14px",
            background: "var(--sl-bg-primary)"
          }}
        >
          <colgroup>
            <col span={1} style={{width: "3%", minWidth: "20px"}} />
            <col span={1} style={{width: "45%", minWidth: "350px"}} />
            <col span={1} style={{width: "30%", minWidth: "240px"}} />
            <col span={1} style={{width: "10%", minWidth: "60px"}} />
            <col span={1} style={{width: "10%", minWdith: "100px"}} />
            <col span={1} style={{width: "2%", minWidth: "20px"}} />
          </colgroup>
          <thead>
            <tr
              style={{
                color: "var(--sl-text-quaternary)",
                fontSize: "12px",
                fontWeight: 600,
                background: "var(--sl-bg-secondary)",
                borderBottom: "1px solid var(--sl-border-secondary)"
              }}
            >
              <th className="px-1">&nbsp;</th>
              <th>Ticket Name</th>
              <th>On Sale Date</th>
              <th>Price</th>
              <th>Inventory</th>
              <th>&nbsp;</th>
            </tr>
          </thead>
          <DragDropContext onDragEnd={(result) => {
            if (!result.destination) {
              return false;
            }
            onMove(result.source.index, result.destination.index, result);
          }}>
            <Droppable droppableId="add-ons-droppable">
              {(provided) => (
                <tbody {...provided.droppableProps} ref={provided.innerRef}>
                  {items.map((item, index) =>
                    <Draggable key={"" + getId(item)} draggableId={"" + getId(item)} index={index} isDragDisabled={isDragDisabled ? isDragDisabled(item) : false}>
                      {(provided, snapshot) => renderRow(item, index, provided, snapshot)}
                    </Draggable>
                  )}
                  {provided.placeholder}
                  {footer}
                </tbody>
              )}
            </Droppable>
          </DragDropContext>
        </table>
      </div>
    </div>
  </div>
);

const AddOnsTable = ({
  csrfToken,
  team,
  currentUser,
  confirm,
  addOns,
  fetchNewAddOn,
  fetchAddOnToEdit,
  deleteAddOn,
  updateAddOn,
  addOnDragged,
  destroySeatingChartEvent,
  canChangeAddOnSeatingChart,
  setDuplicatingAddOnId,
  isSharingInventory,
  seatingChartEvent
}) => {
  const [clearingSeatedAddOns, setClearingSeatedAddOns] = useState(false);

  return (
    <>
      <AddOnsTableFrame
        items={sortByPositionAndName(addOns)}
        getId={(addOn) => addOn.id}
        onMove={(itemIndex, moveIndex, result) => {
          var updated = Object.assign({}, addOns[itemIndex], {
            position: (moveIndex + 1)
          });

          addOnDragged(result);
          updateAddOn(csrfToken, team, updated);
        }}
        renderRow={(addOn, index, provided, snapshot) => (
          <AddOnRow key={index}
                    csrfToken={csrfToken}
                    provided={provided}
                    snapshot={snapshot}
                    addOns={addOns}
                    addOn={addOn}
                    team={team}
                    currentUser={currentUser}
                    confirm={confirm}
                    fetchNewAddOn={fetchNewAddOn}
                    fetchAddOnToEdit={fetchAddOnToEdit}
                    addOnIsSeated={addOnIsSeated}
                    isSharingInventory={isSharingInventory}
                    setDuplicatingAddOnId={setDuplicatingAddOnId}
                    deleteAddOn={deleteAddOn} />
        )}
        footer={
          <tr style={{"borderBottom": "none"}}>
            <td>
              <span>&nbsp;</span>
            </td>
            <td colSpan={2}>
              {!destroySeatingChartEvent ? (
                <Tooltip title="Please contact support to clear this seating chart." placement="top" maxWidth={200}>
                  <a href="#"
                      className="small disabled"
                      onClick={
                        (e) => {
                          e.preventDefault();
                        }
                      }>
                    <strong>Clear Seating Chart</strong>
                  </a>
                </Tooltip>
              ) : addOns.length > 0 && addOns[0].seating_chart_category_key && addOns[0].seating_chart_category_key.length > 0 ? (
                canChangeAddOnSeatingChart ? (
                  <a href="#"
                      className="small"
                      onClick={
                        (e) => {
                          e.preventDefault();

                          if(canChangeAddOnSeatingChart){
                            setClearingSeatedAddOns(true);
                          }
                        }
                      }>
                    <strong>Clear Seating Chart</strong>
                  </a>
                ) : (
                  <Tooltip title="Cannot be cleared once sales have started." placement="top" maxWidth={200}>
                    <a href="#"
                        className="small disabled"
                        onClick={
                          (e) => {
                            e.preventDefault();
                          }
                        }>
                      <strong>Clear Seating Chart</strong>
                    </a>
                  </Tooltip>
                )
              ) : (
                <span>&nbsp;</span>
              )}
            </td>
            <td style={{ color: 'var(--sl-text-tertiary)' }}>
              {`${totalNumberSold(addOns)}/${totalNumberAvailable(addOns)}`}
            </td>
            <td>
              <span>&nbsp;</span>
            </td>
            <td>
              <span>&nbsp;</span>
            </td>
          </tr>
        }
      />
      <Modal
        open={clearingSeatedAddOns}
        onClose={() => setClearingSeatedAddOns(false)}
        type="error"
        title="Clear this seating chart?"
        confirmLabel="Yes, clear seating chart"
        onConfirm={destroySeatingChartEvent}
      >
        <p className="mb-0">
          Clearing this seating chart will remove any seated add-ons.<br /><br />Are you sure?
        </p>
      </Modal>
    </>
  );
};

AddOnsTable.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  addOns: PropTypes.array,
  fetchNewAddOn: PropTypes.func.isRequired,
  fetchAddOnToEdit: PropTypes.func.isRequired,
  deleteAddOn: PropTypes.func.isRequired,
  updateAddOn: PropTypes.func.isRequired,
  addOnDragged: PropTypes.func.isRequired,
  destroySeatingChartEvent: PropTypes.func.isRequired,
  canChangeAddOnSeatingChart: PropTypes.bool,
  setDuplicatingAddOnId: PropTypes.func.isRequired,
  isSharingInventory: PropTypes.func.isRequired,
  seatingChartEvent: PropTypes.object
};

export default AddOnsTable;
